import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import { defaultMetaObj, initialProjectAssignmentState } from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const {
  projectAssignmentEndPoint,
  userProjectAssignmentEndPoint,
  teamProjectAssignmentEndPoint,
} = endPoints;

export const fetchAllProjectAssignments = createAsyncThunk(
  "projectAssignments/fetchAllProjectAssignments",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: projectAssignmentEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllUserProjectAssignments = createAsyncThunk(
  "projectAssignments/fetchAllUserProjectAssignments",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: userProjectAssignmentEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllTeamProjectAssignments = createAsyncThunk(
  "projectAssignments/fetchAllTeamProjectAssignments",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: teamProjectAssignmentEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteProjectAssignment = createAsyncThunk(
  "projectAssignments/deleteProjectAssignment",
  async ({ projectAssignmentId }: any, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(
        projectAssignmentEndPoint,
        projectAssignmentId
      );

      if (response.status === 204) {
        NotificationManager.success("Project Assignment Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const addNewProjectAssignment = createAsyncThunk(
  "projectAssignments/addNewProjectAssignment",
  async (newProjectAssignment: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${projectAssignmentEndPoint}/`,
        newProjectAssignment
      );

      if (response.status === 201) {
        NotificationManager.success("New Project Created Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const assignProjectsToUser = createAsyncThunk(
  "projectAssignments/assignProjectsToUser",
  async (newProjectAssignment: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${userProjectAssignmentEndPoint}/`,
        newProjectAssignment
      );

      if (response.status === 201) {
        NotificationManager.success("Project Assigned Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateAssignProjectsToUser = createAsyncThunk(
  "projectAssignments/updateAssignProjectsToUser",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        userProjectAssignmentEndPoint,
        data?.id,
        data
      );

      if (response.status === 204) {
        NotificationManager.success("Assigned Projects Updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateAssignProjectsToTeam = createAsyncThunk(
  "projectAssignments/updateAssignProjectsToTeam",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        teamProjectAssignmentEndPoint,
        data?.id,
        data
      );

      if (response.status === 204) {
        NotificationManager.success("Assigned Projects Updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateProjectAssignment = createAsyncThunk(
  "projectAssignments/updateProjectAssignment",
  async (
    { updatedProjectAssignment, projectAssignmentId }: any,
    { rejectWithValue }
  ) => {
    try {
      const response: AxiosResponse = await crudService.update(
        projectAssignmentEndPoint,
        projectAssignmentId,
        updatedProjectAssignment
      );
      if (response.status === 204) {
        NotificationManager.success("Project Assignment updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateProjectAssignmentInBulk = createAsyncThunk(
  "projectAssignments/updateProjectAssignmentInBulk",
  async ({ selectedProjects, updatedData }: any, { rejectWithValue }) => {
    try {
      const updatePromises = selectedProjects.map(
        async ({
          project_id,
          assigned_user,
          assigned_team,
          ...remainingData
        }: any) => {
          try {
            const modifiedData = updatedData?.assigned_user
              ? {
                  ...remainingData,
                  project_id,
                  assigned_team,
                  assigned_user: [...assigned_user, updatedData?.assigned_user],
                }
              : {
                  ...remainingData,
                  project_id,
                  assigned_user,
                  assigned_team: [...assigned_team, updatedData?.assigned_team],
                };

            const response: AxiosResponse = await crudService.update(
              projectAssignmentEndPoint,
              project_id,
              modifiedData
            );
            return response.status;
          } catch (error) {
            const errorMessage = getErrorMessage(error);

            NotificationManager.error(errorMessage);
          }
        }
      );
      const responses = await Promise.all(updatePromises);

      if (responses.every((res: any) => res === 204)) {
        NotificationManager.success("Projects Assigned Successfully.");
      }
      return responses;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const projectAssignmentSlice = createSlice({
  name: "projectAssignments",
  initialState: initialProjectAssignmentState,
  reducers: {
    setToDefaultState: (state: any) => {
      state.userProjectAssignments = [];
      state.teamProjectAssignments = [];
      state.projectAssignmentId = [];
    },
  },
  extraReducers: (builder) => {
    // fetch all Project Assignment
    builder
      .addCase(fetchAllProjectAssignments.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllProjectAssignments.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.projectAssignments = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllProjectAssignments.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.projectAssignments = [];
          state.meta = defaultMetaObj;
        }
      );

    // fetch all user Project Assignment
    builder
      .addCase(fetchAllUserProjectAssignments.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllUserProjectAssignments.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.userProjectAssignments = action.payload?.objects[0]
            ?.projects as any[];
          state.projectAssignmentId = action.payload?.objects[0]?.id || null;
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllUserProjectAssignments.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.projectAssignments = [];
          state.meta = defaultMetaObj;
        }
      );

    // fetch all team Project Assignment
    builder
      .addCase(fetchAllTeamProjectAssignments.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllTeamProjectAssignments.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.teamProjectAssignments = action.payload?.objects[0]
            ?.assigned_projects as any[];
          state.projectAssignmentId = action.payload?.objects[0]?.id || null;
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllTeamProjectAssignments.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.projectAssignments = [];
          state.meta = defaultMetaObj;
        }
      );

    // delete Project Assignment
    builder
      .addCase(deleteProjectAssignment.pending, (State) => {
        State.loading = true;
      })
      .addCase(
        deleteProjectAssignment.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        deleteProjectAssignment.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // add new Project Assignment
    builder
      .addCase(addNewProjectAssignment.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        addNewProjectAssignment.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(addNewProjectAssignment.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    //   update Project Assignment
    builder
      .addCase(updateProjectAssignment.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateProjectAssignment.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateProjectAssignment.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    //   update user Project Assignment
    builder
      .addCase(updateAssignProjectsToUser.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateAssignProjectsToUser.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateAssignProjectsToUser.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    //   update team Project Assignment
    builder
      .addCase(updateAssignProjectsToTeam.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateAssignProjectsToTeam.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateAssignProjectsToTeam.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    //   update Project Assignment in builk
    builder
      .addCase(updateProjectAssignmentInBulk.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateProjectAssignmentInBulk.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateProjectAssignmentInBulk.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const projectAssignmentReducer = projectAssignmentSlice.reducer;
export const { setToDefaultState } = projectAssignmentSlice.actions;
